// Copyright Epic Games, Inc. All Rights Reserved.

import { useQueryParams } from "../routing/useQueryParams";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setMainServiceAccessToken } from "../../store/shared/actions";
import { selectMainServiceAccessToken } from "../../store/shared/selectors";

interface IMainServiceAccessTokenQuery {
  mainServiceAccessToken: string;
}

/**
 * @desc Retrieves mainServiceAccessToken query param and sets it to store if
 * received new access token that does not equal to stored one. Will return an
 * access token from a query if such exists, otherwise returns one from store.
 *
 * Thus we make sure that we can use same access token on pages when we don't receive
 * an access token via query string, e.g., we were redirected to a page from
 * other SPA page. In the same time, we always prefer to use new token received
 * from the query, rather than the stored one.
 */
export function useMainServiceAccessToken(): string {
  const {
    mainServiceAccessToken,
  } = useQueryParams<IMainServiceAccessTokenQuery>();
  const dispatch = useDispatch();
  const storedMainServiceAccessToken = useSelector(
    selectMainServiceAccessToken
  );

  useEffect(() => {
    if (
      mainServiceAccessToken &&
      storedMainServiceAccessToken !== mainServiceAccessToken
    ) {
      dispatch(setMainServiceAccessToken(mainServiceAccessToken));
    }
  }, [mainServiceAccessToken, storedMainServiceAccessToken]);

  return mainServiceAccessToken || storedMainServiceAccessToken;
}
